/**
 * App-wide feature-flag verdicts for the current user.
 *
 * Verdicts are fetched once per login from the backend (which owns evaluation +
 * granting). Mirrors the dedup discipline of useCreditStore.checkSignupBonus so
 * multiple tabs / effect firings coalesce into one request. Fail-safe: any error
 * leaves all flags off — a backend hiccup never blocks the app.
 */

import { create } from "zustand";
import { fetchFeatureFlags, type FeatureFlagVerdicts } from "./featureFlagApi";

// Module-level dedup (not store state) — survives re-renders, keyed by uid.
let loadedForUid: string | null = null;
let inFlightUid: string | null = null;
let inFlight: Promise<FeatureFlagVerdicts> | null = null;

interface FeatureFlagState {
  flags: FeatureFlagVerdicts;
  loaded: boolean;
  loadedUid: string | null;
  loadFlags: (uid: string) => Promise<void>;
  reset: () => void;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set) => ({
  flags: {},
  loaded: false,
  loadedUid: null,

  loadFlags: async (uid: string) => {
    if (!uid) return;
    // Already loaded for this uid in this session.
    if (loadedForUid === uid) return;
    // Same uid already in flight — await the existing request.
    if (inFlight && inFlightUid === uid) {
      await inFlight;
      return;
    }

    inFlightUid = uid;
    inFlight = (async () => {
      try {
        const flags = await fetchFeatureFlags();
        if (inFlightUid === uid) {
          loadedForUid = uid;
          set({ flags, loaded: true, loadedUid: uid });
        }
        return flags;
      } catch (err) {
        console.error("[featureFlags] loadFlags failed:", err);
        // Fail safe: leave flags empty/off, allow a later retry.
        if (inFlightUid === uid) set({ flags: {}, loaded: true, loadedUid: uid });
        return {};
      } finally {
        if (inFlightUid === uid) {
          inFlight = null;
          inFlightUid = null;
        }
      }
    })();

    await inFlight;
  },

  reset: () => {
    loadedForUid = null;
    inFlightUid = null;
    inFlight = null;
    set({ flags: {}, loaded: false, loadedUid: null });
  },
}));
